import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { maxEndppointOIDCUsersQueryOptions } from '#api/endpoint-oidc-users.queries.ts';
import type { Host } from '#dn-types/host.ts';

/**
 * Maps device owner IDs to emails for a page of hosts.
 *
 * There is no endpoint for fetching a single owner, so this drains the whole list — up to ten
 * sequential pages. That stays off the critical path: the query is disabled unless a host on the
 * page actually has an owner, callers render fine without it, and rows enrich once it lands. A
 * failed drain is left to resolve to an empty map rather than surfacing an error.
 */
export function useDeviceOwnerEmails(hosts: Host[]): ReadonlyMap<string, string> {
  const hasEndpointDevices = hosts.some((host) => host.endpointOIDCUserID);

  const { data } = useQuery({
    ...maxEndppointOIDCUsersQueryOptions,
    enabled: hasEndpointDevices,
  });

  // Built once here rather than per row, so each row is a map hit instead of a scan of every owner.
  return useMemo(() => new Map(data?.data.map((user) => [user.id, user.email])), [data]);
}
